import React, { useState, useEffect } from "react";
import { Alert, Modal, Button, Form } from "react-bootstrap";
import axios from '../../../api/axios';
import { getFallbackProductImage, resolveProductImage } from '../../../data/productsApi';

type CategoryOption = {
  _id: string;
  name: string;
};

type ProductItem = {
  _id: string;
  name: string;
    companyName?: string;
  description: string;
  price: number;
  stock: number;
  sku?: string;
    subcategory?: string;
    sizes?: string[];
  tags?: string[];
  images?: string[];
  discount?: number;
    isFeatured?: boolean;
  isActive: boolean;
  category?: { _id?: string; name?: string } | string;
};

type EditProductModalProps = {
  show: boolean;
  setShow: (value: boolean) => void;
  product: ProductItem | null;
  token: string | null;
  categories: CategoryOption[];
  onSaved: () => Promise<void> | void;
};

const EditProductModal = ({ show, setShow, product, token, categories, onSaved }: EditProductModalProps) => {
    const [error, setError] = useState('');
    const [updatedProduct, setUpdatedProduct] = useState({
        name: '',
        companyName: '',
        description: '',
        price: '',
        category: '',
        subcategory: '',
        sizes: '',
        stock: '',
        sku: '',
        tags: '',
        discount: '',
        isActive: true,
        isFeatured: false,
    });
    const [imageSlots, setImageSlots] = useState<Array<File | null>>([null, null, null, null, null]);

    useEffect(() => {
        if (product) {
            setUpdatedProduct({
                name: product.name || '',
                companyName: product.companyName || '',
                description: product.description || '',
                price: String(product.price ?? ''),
                category: typeof product.category === 'object' ? (product.category?._id || '') : (product.category || ''),
                subcategory: product.subcategory || '',
                sizes: Array.isArray(product.sizes) ? product.sizes.join(', ') : '',
                stock: String(product.stock ?? ''),
                sku: product.sku || '',
                tags: Array.isArray(product.tags) ? product.tags.join(', ') : '',
                discount: product.discount !== undefined ? String(product.discount) : '',
                isActive: product.isActive ?? true,
                isFeatured: product.isFeatured ?? false,
            });
            setImageSlots([null, null, null, null, null]);
        }
    }, [product]);

    const setImageAt = (index: number, file: File | null) => {
        setImageSlots((prev) => prev.map((item, i) => (i === index ? file : item)));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
        setUpdatedProduct({ ...updatedProduct, [name]: type === "checkbox" ? checked : value });
    };

    const handleUpdate = async () => {
        if (!product?._id) {
            return;
        }

        if (!token) {
            setError('Authentication is required. Please login again.');
            return;
        }

        const selectedImages = imageSlots.filter((file): file is File => Boolean(file));

        setError('');

        try {
            const formData = new FormData();
            formData.append('name', updatedProduct.name.trim());
            if (updatedProduct.companyName.trim()) {
                formData.append('companyName', updatedProduct.companyName.trim());
            }
            formData.append('description', updatedProduct.description.trim());
            formData.append('price', String(Number(updatedProduct.price)));
            formData.append('category', updatedProduct.category);
            formData.append('subcategory', updatedProduct.subcategory.trim());
            formData.append('sizes', updatedProduct.sizes);
            formData.append('stock', String(Number(updatedProduct.stock)));
            if (updatedProduct.sku.trim()) {
                formData.append('sku', updatedProduct.sku.trim());
            }
            if (updatedProduct.tags.trim()) {
                formData.append('tags', updatedProduct.tags);
            }
            if (updatedProduct.discount) {
                formData.append('discount', String(Number(updatedProduct.discount)));
            }
            formData.append('isActive', String(updatedProduct.isActive));
            formData.append('isFeatured', String(updatedProduct.isFeatured));
            selectedImages.forEach((file) => formData.append('images', file));

            await axios.put(
                `/products/${product._id}`,
                formData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );

            await onSaved();
            setShow(false);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to update product.');
        }
    };

    return (
        <Modal show={show} onHide={() => setShow(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Edit Product</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form>
                    <Form.Group className="mb-2">
                        <Form.Label>Name</Form.Label>
                        <Form.Control type="text" name="name" value={updatedProduct?.name || ""} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Description</Form.Label>
                        <Form.Control as="textarea" name="description" value={updatedProduct?.description || ""} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Company Name (optional)</Form.Label>
                        <Form.Control type="text" name="companyName" value={updatedProduct?.companyName || ""} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Price</Form.Label>
                        <Form.Control type="number" name="price" value={updatedProduct?.price || ""} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Category</Form.Label>
                        <Form.Select name="category" value={updatedProduct?.category || ""} onChange={handleChange}>
                            <option value="">Select category</option>
                            {categories.map((category) => (
                                <option key={category._id} value={category._id}>{category.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Subcategory</Form.Label>
                        <Form.Control type="text" name="subcategory" value={updatedProduct?.subcategory || ""} onChange={handleChange} placeholder="Optional subcategory" />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Sizes (comma separated)</Form.Label>
                        <Form.Control type="text" name="sizes" value={updatedProduct?.sizes || ""} onChange={handleChange} placeholder="Optional sizes" />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Stock</Form.Label>
                        <Form.Control type="number" name="stock" value={updatedProduct?.stock || ""} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>SKU</Form.Label>
                        <Form.Control type="text" name="sku" value={updatedProduct?.sku || ""} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Tags (comma separated)</Form.Label>
                        <Form.Control type="text" name="tags" value={updatedProduct?.tags || ""} onChange={handleChange} />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Check
                            type="checkbox"
                            label="Active"
                            name="isActive"
                            checked={updatedProduct?.isActive || false}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Check
                            type="checkbox"
                            label="Featured"
                            name="isFeatured"
                            checked={updatedProduct?.isFeatured || false}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    {Array.isArray(product?.images) && product.images.length > 0 ? (
                        <Form.Group className="mb-2">
                            <Form.Label>Current Images</Form.Label>
                            <div className="d-flex gap-2 flex-wrap">
                                {product.images.map((img, index) => {
                                    const imageUrl = resolveProductImage(img);
                                    return (
                                        <img
                                            key={`${img}-${index}`}
                                            src={imageUrl}
                                            alt={`Current ${index + 1}`}
                                            onError={(event) => {
                                                const target = event.currentTarget;
                                                if (target.src !== getFallbackProductImage()) {
                                                    target.src = getFallbackProductImage();
                                                }
                                            }}
                                            width={56}
                                            height={56}
                                            style={{ objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }}
                                        />
                                    );
                                })}
                            </div>
                            <Form.Text muted>Leave all upload fields empty to keep these existing images.</Form.Text>
                        </Form.Group>
                    ) : null}

                    <Form.Group className="mb-2" controlId="editProductImagesPrimary">
                        <Form.Label>Primary Image (optional on edit)</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(0, input.files?.[0] || null);
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="editProductImages2">
                        <Form.Label>Optional Image 2</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(1, input.files?.[0] || null);
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="editProductImages3">
                        <Form.Label>Optional Image 3</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(2, input.files?.[0] || null);
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="editProductImages4">
                        <Form.Label>Optional Image 4</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(3, input.files?.[0] || null);
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="editProductImages5">
                        <Form.Label>Optional Image 5</Form.Label>
                        <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const input = event.target as HTMLInputElement;
                                setImageAt(4, input.files?.[0] || null);
                            }}
                        />
                        <Form.Text muted>{imageSlots.filter(Boolean).length} new file(s) selected</Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-2">
                        <Form.Label>Discount %</Form.Label>
                        <Form.Control type="number" min="0" max="100" name="discount" value={updatedProduct?.discount || ""} onChange={handleChange} />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleUpdate}>Update</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditProductModal;

